import styled from '@emotion/styled';
import { SEO, isBrowserIE, useSiteMetadata } from '@lucid/gatsby-theme-lucid';
import { ThemeProvider } from 'emotion-theming';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import { trackCustomEvent } from 'gatsby-plugin-google-analytics';
import React, {
  FC,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useReducer,
  useState
} from 'react';
import { Helmet } from 'react-helmet';
import Modal from 'react-modal';
import ReactPlayer from 'react-player';

import xIcon from 'assets/images/consideration/x.svg';
import AbTest, { PossibleTrigger } from 'components/ab-test/ab-test';
import AlertBanner from 'components/alert-banner/alert-banner';
import LayoutContext from 'contexts/layoutContext';
import { Drupal_Metatag, Drupal_Node, Maybe } from 'generated/graphql';
import theme from 'theme/theme';
import themeNavAbTest from 'theme/theme-nav-ab-test';
import { Tracker } from 'types/types';
import { hideDrift, resetDrift } from 'utils/drift';
import { isBrowser } from 'utils/helpers';
import AbTestAdminUi from './ab-test-admin-ui/ab-test-admin-ui';
import AbTestContext from '../contexts/abTestContext';
import { languages } from '../../data/languages';
import '../stylesheets/_base.scss';
import '../stylesheets/_layout.scss';
import './layout.scss';

interface AbTest {
  [key: string]: string;
}

interface AbTestObj {
  [key: string]: AbTest;
}

interface Props {
  langPrefix: string;
  entityTranslations?: Drupal_Node[];
  metatags?: Array<Maybe<Drupal_Metatag>> | null;
  location: {
    pathname: string;
    origin: string;
  };
  pageType: string;
  children: ReactNode;
  nid?: string;
  trackers?: Tracker;
  hideSsr: boolean;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const metricLoggerHomepage = 'Homepage';
const metricLoggerContact = 'Contact';
const metricLoggerEnterprise = 'Enterprise';
const metricLoggerEducation = 'Education';
const metricLoggerLandingFlowchart = 'LandingFlowchart';
const metricLoggerLandingVisioAlternative = 'LandingVisioAlternative';
const metricLoggerLandingOrgChart = 'LandingOrgChart';

const metricLoggerPages = {
  en: {
    '/': metricLoggerHomepage,
    '/pages': metricLoggerHomepage,
    '/pages/home': metricLoggerHomepage,
    '/pages/landing': metricLoggerHomepage,
    '/pages/landing/flowchart_software': metricLoggerLandingFlowchart,
    '/pages/landing/visio_alternative': metricLoggerLandingVisioAlternative,
    '/pages/landing/org-chart-software': metricLoggerLandingOrgChart,
    '/pages/contact': metricLoggerContact,
    '/pages/enterprise': metricLoggerEnterprise,
    '/pages/usecase/education': metricLoggerEducation
  },
  fr: {
    '/pages/fr': metricLoggerHomepage,
    '/pages/home': metricLoggerHomepage,
    '/pages/fr/landing': metricLoggerHomepage,
    '/pages/fr/contacter-lucidchart': metricLoggerContact,
    '/pages/fr/compte-entreprise-lucidchart': metricLoggerEnterprise,
    '/pages/fr/education': metricLoggerEducation
  },
  de: {
    '/pages/de': metricLoggerHomepage,
    '/pages/de/home': metricLoggerHomepage,
    '/pages/de/landing': metricLoggerHomepage,
    '/pages/de/kontakt-zu-lucidchart': metricLoggerContact,
    '/pages/de/lucidchart-fur-unternehmen': metricLoggerEnterprise,
    '/pages/de/bildung': metricLoggerEducation
  },
  ja: {
    '/pages/ja': metricLoggerHomepage,
    '/pages/ja/home': metricLoggerHomepage,
    '/pages/ja/landing': metricLoggerHomepage,
    '/pages/ja/enterprise': metricLoggerEnterprise,
    '/pages/ja/lucidchart-for-education': metricLoggerEducation
  },
  pt: {
    '/pages/pt': metricLoggerHomepage,
    '/pages/pt/home': metricLoggerHomepage,
    '/pages/pt/landing': metricLoggerHomepage,
    '/pages/pt/entre-em-contato-com-o-lucidchart': metricLoggerContact,
    '/pages/pt/lucidchart-para-empresas': metricLoggerEnterprise,
    '/pages/pt/educacao': metricLoggerEducation
  },
  es: {
    '/pages/es': metricLoggerHomepage,
    '/pages/es/home': metricLoggerHomepage,
    '/pages/es/landing': metricLoggerHomepage,
    '/pages/es/comunicate-con-lucidchart': metricLoggerContact,
    '/pages/es/lucidchart-para-empresas': metricLoggerEnterprise,
    '/pages/es/lucidchart-para-la-educacion': metricLoggerEducation
  },
  nl: {
    '/pages/nl': metricLoggerHomepage,
    '/pages/nl/home': metricLoggerHomepage,
    '/pages/nl/landing': metricLoggerHomepage,
    '/pages/nl/contact-opnemen-met-lucidchart': metricLoggerContact,
    '/pages/nl/lucidchart-voor-ondernemingen': metricLoggerEnterprise,
    '/pages/nl/onderwijs': metricLoggerEducation
  }
} as {
  [key: string]: {
    [key: string]: string;
  };
};

const i18nCollapseAt: { [key: string]: string } = {
  de: '1135px',
  fr: '1185px',
  ja: '1170px',
  'pt-br': '1055px',
  es: '1125px',
  nl: '1158px'
};

const Layout: FC<Props> = ({
  langPrefix,
  location,
  pageType,
  children,
  hideSsr,
  metatags = [],
  entityTranslations = [],
  nid = '',
  trackers = null
}) => {
  const {
    optimizelyHash: { hash }
  } = useStaticQuery(
    graphql`
      {
        optimizelyHash {
          hash
        }
      }
    `
  );

  const { cmsUrl } = useSiteMetadata();

  const [modal, setModal] = useState<JSX.Element | null>(null);
  const [videoModalUrl, setVideoModalUrl] = useState('');
  const [videoAutoPlay, setVideoAutoPlay] = useState(false);
  const [videoConfig, setVideoConfig] = useState({});
  const [visibility, setVisibility] = useState(!hideSsr);
  const [showEditButton, setShowEditButton] = useState(false);
  const [showAbtestButtons, setShowAbtestButtons] = useState(false);

  const [abTests, updateTest] = useReducer(
    (tempAbTests: AbTestObj, abTest: AbTest) => {
      const theseAbTests = { ...tempAbTests };
      const { test } = abTest;
      const currentTest = tempAbTests[test] || {};

      Object.keys(abTest).forEach(key => {
        currentTest[key] = abTest[key];
      });

      theseAbTests[test] = currentTest;

      return theseAbTests;
    },
    {}
  );

  useEffect(() => {
    setShowEditButton(window.localStorage.getItem('showEditLink') === '1');
    setShowAbtestButtons(
      window.localStorage.getItem('showAbTestingUi') === '1'
    );
  }, []);

  useEffect(() => {
    if (trackers) {
      const trackerKey = 'mktgTrackers';
      let existingTrackers: Record<string, string[]> = {};
      try {
        const tracker = window.localStorage.getItem(trackerKey);
        if (tracker) {
          existingTrackers = JSON.parse(tracker);
        }
      } catch (error) {
        console.error(error);
      }

      const newTrackers = Object.keys(trackers).reduce(
        (updatedTrackers, key) => {
          let fieldValue = trackers[key];
          if (fieldValue) {
            if (!Array.isArray(fieldValue)) {
              fieldValue = [fieldValue];
            }
            const newValues = fieldValue.map(
              value => value!.entity!.entityLabel!
            );
            if (newValues) {
              // send to GA
              newValues.forEach(value => {
                trackCustomEvent({
                  category: trackerKey,
                  action: key,
                  label: value
                });
              });

              // merge with existing values to be saved in local storage
              const existingValues = updatedTrackers[key] || [];
              const newSet = new Set([...newValues, ...existingValues]);
              // eslint-disable-next-line no-param-reassign
              updatedTrackers[key] = Array.from(newSet);
            }
          }
          return updatedTrackers;
        },
        existingTrackers
      );
      window.localStorage.setItem(trackerKey, JSON.stringify(newTrackers));
    }
  }, [trackers]);

  const openModal = (html: JSX.Element) => {
    setModal(html);
  };

  const closeModal = () => setModal(null);

  const getVideoModal = (
    thisVideoModalUrl: string,
    autoPlay = false,
    thisVideoConfig = {}
  ) => {
    setVideoModalUrl(thisVideoModalUrl);
    setVideoAutoPlay(autoPlay);
    setVideoConfig(thisVideoConfig);
  };

  const { shortLangPrefix, i18nKey, urlLangPrefix } = languages[langPrefix];

  const layoutContext = {
    language: {
      langPrefix,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey
    },
    location,
    pageType,
    video: {
      getVideoModal
    },
    modal: {
      openModal
    },
    abTesting: {
      updateTest
    }
  };

  useEffect(() => {
    const metricLoggerString =
      (metricLoggerPages[shortLangPrefix] || {})[
      window.location.pathname.replace(/\/$/, '')
      ] || 'Other';

    try {
      const markLoaded = (delayed?: boolean) => {
        const metricLogger = window.lucid?.getMetricLogger?.(
          metricLoggerString
        )!;
        metricLogger.markLoaded();
        metricLogger.tag('path', window.location.pathname);
        metricLogger.tag('corporate', true);
        metricLogger.tag('delayed', delayed);
      };

      if (window.lucid?.getMetricLogger) {
        markLoaded();
      } else {
        const checkInterval = setInterval(() => {
          if (window.lucid && window.lucid.getMetricLogger) {
            markLoaded(true);
            clearInterval(checkInterval);
          }
        }, 17);
      }
    } catch (e) {
      console.log('Metric logger failure', e);
    }

    Modal.setAppElement('body');
  }, [shortLangPrefix]);

  useEffect(() => {
    if (pageType !== '404') {
      resetDrift();
    }

    return () => {
      hideDrift();
    };
  }, [pageType]);

  useEffect(() => {
    const mobileThreshold = 768;
    const mobileTimeout = 4000;
    const desktopTimeout = 1000;
    const timeoutInMs =
      window.innerWidth >= mobileThreshold ? desktopTimeout : mobileTimeout;
    let safetyTimeoutId: number;

    if (hideSsr) {
      const state = window.optimizely?.get('state');
      const activeExperiments = state?.getActiveExperimentIds() || [];

      if (activeExperiments?.length === 0 || window.optimizelyActionApplied) {
        setVisibility(true);
      } else {
        safetyTimeoutId = window.setTimeout(() => {
          setVisibility(true);
        }, timeoutInMs);
        Object.defineProperty(window, 'optimizelyActionApplied', {
          set(value) {
            if (value) {
              window.clearTimeout(safetyTimeoutId);
              setVisibility(true);
            }
          },
          configurable: true
        });
      }
    }

    return () => {
      window.clearTimeout(safetyTimeoutId);
      Object.defineProperty(window, 'optimizelyActionApplied', {
        value: false,
        writable: true,
        configurable: true
      });
    };
  }, [hideSsr]);

  const handleCloseModal = (e: SyntheticEvent) => {
    e.preventDefault();
    setVideoModalUrl('');
  };

  const customTheme = { ...theme };
  customTheme.navbar = { ...theme.navbar };
  if (langPrefix !== 'en' && i18nCollapseAt[langPrefix]) {
    customTheme.navbar.collapseAt = i18nCollapseAt[langPrefix];
  }

  const optimizelyScript = (
    <script defer={!hideSsr} src={withPrefix(`/optimizely-${hash}.js`)} />
  );

  return (
    <div
      className="gatsby-root"
      style={{ visibility: visibility ? 'visible' : 'hidden' }}
    >
      <EmotionLayoutContextProvider value={layoutContext}>
        <SEO
          entityMetatags={metatags as Drupal_Metatag[]}
          entityTranslations={entityTranslations}
          location={location}
          pageType={pageType}
          shortLangPrefix={shortLangPrefix}
        />
        <AbTest
          aboveTheFold
          conditionalTrigger={shortLangPrefix === 'en'}
          test="Y20Q3_RolloutNewMarketingNavFooter"
          trigger={PossibleTrigger.Load}
        >
          <AbTestContext.Consumer>
            {({ branch }) => (
              <ThemeProvider
                theme={branch === 'T_A' ? customTheme : themeNavAbTest}
              >
                {children}
                <AlertBanner
                  condition={isBrowserIE()}
                  storageKey="ie11-unsupported"
                  type="alert"
                >
                  As of October 5th, 2020
                  {' '}
                  <strong>
                    Lucidchart will no longer support Internet Explorer 11.
                  </strong>
                  {' '}
                  Click the link to learn how to upgrade to a supported browser.
                </AlertBanner>
                {videoModalUrl && (
                  <Modal
                    isOpen
                    shouldCloseOnOverlayClick
                    ariaHideApp={false}
                    className="video-modal__content"
                    onRequestClose={e => handleCloseModal(e)}
                    overlayClassName="video-modal__overlay"
                    portalClassName="video-modal"
                  >
                    <div className="video-modal__container">
                      <ReactPlayer
                        controls
                        config={videoConfig}
                        height="100%"
                        playing={videoAutoPlay}
                        url={videoModalUrl}
                        width="100%"
                      />
                    </div>
                    <button
                      className="video-modal__button"
                      onClick={e => handleCloseModal(e)}
                      onKeyPress={e => handleCloseModal(e)}
                      tabIndex={0}
                      type="button"
                    >
                      <img
                        alt="close"
                        className="video-modal__exit"
                        src={xIcon}
                        width="20"
                      />
                    </button>
                  </Modal>
                )}
                {modal && (
                  <Modal
                    isOpen
                    shouldCloseOnOverlayClick
                    ariaHideApp={false}
                    className="modal__content"
                    onRequestClose={() => closeModal()}
                    overlayClassName="modal__overlay"
                    portalClassName="modal"
                  >
                    <div className="modal__container">
                      {modal}

                      <button
                        className="modal__exit"
                        onClick={() => closeModal()}
                        onKeyPress={() => closeModal()}
                        tabIndex={0}
                        type="button"
                      />
                    </div>
                  </Modal>
                )}

                {showEditButton && (
                  <a
                    className="cms-edit-link"
                    href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`}
                  >
                    Edit Page
                  </a>
                )}

                {showAbtestButtons && Object.keys(abTests).length > 0 && (
                  <AbTestAdminUi abTests={abTests as any} />
                )}
              </ThemeProvider>
            )}
          </AbTestContext.Consumer>
        </AbTest>
      </EmotionLayoutContextProvider>
      {!isBrowser() &&
        (!hideSsr ? (
          optimizelyScript
        ) : (
            /* eslint-disable-next-line react/jsx-indent */
            <Helmet key={`${pageType}-optimizelyScript`}>
              {optimizelyScript}
            </Helmet>
          ))}
    </div>
  );
};

export default Layout;

export const query = graphql`
  fragment link on Drupal_ParagraphLink {
    fieldOpenNewTab
    fieldBeaconElementDetail
    fieldCta {
      url {
        ...getLinkPath
      }
      title
    }
  }

  fragment extraTranslations on Drupal_ParagraphExtraTranslations {
    fieldTranslationNode {
      entity {
        ...entityTranslations
      }
    }
    fieldPreferReferenceTranslati
  }

  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
    fieldMarketoConfirmBody
    fieldMarketoConfirmHeading
  }

  fragment menuFragment on Drupal_MenuLinkContent {
    menuName
    parent
    entityUuid
    entityLanguage {
      id
    }
    title
    link {
      url {
        path
      }
    }
    ... on Drupal_MenuLinkContentFooter {
      fieldBeaconElementDetail
      fieldKmEventName
      fieldKey
      fieldMarketo {
        entity {
          ...marketo
        }
      }
    }
    ... on Drupal_MenuLinkContentDefaultFooter {
      fieldBeaconElementDetail
      fieldKmEventName
      fieldKey
      fieldMarketo {
        entity {
          ...marketo
        }
      }
    }
    ... on Drupal_MenuLinkContentMainNavLinks {
      fieldBeaconElementDetail
      fieldKmEventName
    }
    ... on Drupal_MenuLinkContentIntegrationPpcNavbar {
      fieldKey
      fieldKmEventName
    }
    ... on Drupal_MenuLinkContentMoreExamplesList {
      fieldTranslatedLink
      fieldCategoryImage
    }
  }
`;
